import type { AssignableRole } from "@/shared/organizations/access-client";
import type { ExistingMemberRole } from "@/shared/organizations/access-errors";
import { MembershipRole, type AdminMembership } from "@/shared/organizations/membership-types";

export const ASSIGNABLE_ROLES: readonly AssignableRole[] = ["ADMIN", "STAFF"];

/** The API said the user is already a member although the screen did not show it. */
export interface AddConflict {
  readonly organizationId: string;
  readonly role: ExistingMemberRole | null;
}

export interface AddToOrganizationView {
  readonly existing: AdminMembership | undefined;
  readonly inactiveMembership: boolean;
  readonly alreadyMember: boolean;
  readonly assigningMore: boolean;
  readonly pendingConflict: boolean;
  readonly blocked: boolean;
  readonly alreadyRole: ExistingMemberRole | null;
  readonly roleOptions: readonly AssignableRole[];
  readonly role: AssignableRole;
  readonly activeLocationIds: readonly string[];
}

/**
 * What the form should show for the picked organization, derived only from
 * the memberships on screen. Any row, whatever its status, counts: an
 * inactive one must be reactivated, not duplicated.
 */
export function deriveAddToOrganizationView(input: {
  readonly memberships: readonly AdminMembership[];
  readonly organizationId: string | null;
  readonly conflict: AddConflict | null;
  readonly chosenRole: AssignableRole;
  readonly canGrantAdmin: boolean;
}): AddToOrganizationView {
  const { memberships, organizationId, conflict, chosenRole, canGrantAdmin } = input;
  const existing = organizationId
    ? memberships.find((m) => m.organization.id === organizationId)
    : undefined;
  const inactiveMembership = existing?.status === "INACTIVE";
  const alreadyMember =
    existing !== undefined && !inactiveMembership && existing.role !== MembershipRole.STAFF;
  const assigningMore = existing?.role === MembershipRole.STAFF && !inactiveMembership;
  const pendingConflict =
    conflict !== null && conflict.organizationId === organizationId && existing === undefined;
  return {
    existing,
    inactiveMembership,
    alreadyMember,
    assigningMore,
    pendingConflict,
    blocked: alreadyMember || inactiveMembership || pendingConflict,
    alreadyRole: (alreadyMember ? (existing?.role as ExistingMemberRole) : conflict?.role) ?? null,
    roleOptions: ASSIGNABLE_ROLES.filter((r) => r !== "ADMIN" || canGrantAdmin),
    role: assigningMore || !canGrantAdmin ? "STAFF" : chosenRole,
    activeLocationIds: (existing?.locations ?? []).filter((l) => l.active).map((l) => l.id),
  };
}
